/**
 * Constants for Roof Panel Management
 * Must match seed.sql values in database
 */

// Re-export shared status codes
export {
  STATUS_CODE,
  type StatusCode,
  isValidStatusCode,
} from "../../../../shared/models";

// ============================================
// Part Type IDs (from PartType table)
// ============================================

export const PART_TYPE_IDS = {
  // Container types
  EAF_ONLINE: 1,
  EAF_OFFLINE: 2,
  ROOF: 3,
  // Spares container types
  ROOF_SPARES: 4,
  SPIDER_SPARES: 5,
  FOURTH_HOLE_PANEL_SPARES: 6,
  FOURTH_HOLE_ELBOW_SPARES: 7,
  /** Unified 55° panel spares (accepts panels for both South 55 and North 55 positions) */
  FIFTY_FIVE_SPARES: 8,
  /** Unified 60° panel spares (accepts panels for both South 60 and North 60 positions) */
  SIXTY_SPARES: 9,
  /** Unified Skirt panel spares (accepts panels for both South Skirt and North Skirt positions) */
  SKIRT_SPARES: 10,
  DELTA_RING_SPARES: 11,
  CENTER_60_SPARES: 12,
  ELBOW_SHIELD_SPARES: 16,
  // Panel types (replaceable) - IDs 100+
  PANEL: 100,
  SPIDER: 101,
  FOURTH_HOLE_PANEL: 102,
  /** Alias for FOURTH_HOLE_PANEL - 4th Hole Panel includes integrated skirt */
  FOURTH_HOLE_PANEL_SKIRT: 102,
  FOURTH_HOLE_ELBOW: 103,
  /** Unified 55° panel type - can be installed in South 55 or North 55 position */
  FIFTY_FIVE_DEGREE: 104,
  /** Unified 60° panel type - can be installed in South 60 or North 60 position */
  SIXTY_DEGREE: 105,
  /** Unified Skirt panel type - can be installed in South Skirt or North Skirt position */
  SKIRT: 106,
  DELTA_RING: 107,
  CENTER_60_DEGREE: 108,
  ELBOW_SHIELD: 112,
} as const satisfies Record<string, number>;

/** Type for part type ID values */
export type PartTypeId = (typeof PART_TYPE_IDS)[keyof typeof PART_TYPE_IDS];

// ============================================
// Part IDs (from Part table)
// ============================================

export const PART_IDS = {
  // Container IDs
  EAF_ONLINE: 1,
  EAF_OFFLINE: 2,
  // Spares container IDs
  ROOF_SPARES: 10,
  /** Container for all panel spares */
  ROOF_PARTS_SPARES: 11,
  SPIDER_SPARES: 20,
  FOURTH_HOLE_PANEL_SPARES: 21,
  FOURTH_HOLE_ELBOW_SPARES: 22,
  /** Unified 55° spares container */
  FIFTY_FIVE_SPARES: 23,
  /** Unified 60° spares container */
  SIXTY_SPARES: 24,
  /** Unified Skirt spares container */
  SKIRT_SPARES: 25,
  DELTA_RING_SPARES: 26,
  CENTER_60_SPARES: 27,
  ELBOW_SHIELD_SPARES: 31,
  // Roof IDs (100+)
  ROOF_4: 100,
  ROOF_5: 101,
  ROOF_6: 102,
} as const satisfies Record<string, number>;

/** Type for part ID values */
export type PartId = (typeof PART_IDS)[keyof typeof PART_IDS];

// ============================================
// Container Names for Display
// ============================================

export const CONTAINER_NAMES = {
  EAF_ONLINE: "EAF-Online",
  EAF_OFFLINE: "EAF-Offline",
  ROOF_SPARES: "Roof Spares",
  ROOF_PARTS_SPARES: "Roof Parts Spares",
  SPIDER_SPARES: "Spider Spares",
  FOURTH_HOLE_PANEL_SPARES: "4th Hole Panel Spares",
  FOURTH_HOLE_ELBOW_SPARES: "4th Hole Elbow Spares",
  FIFTY_FIVE_SPARES: "55° Spares",
  SIXTY_SPARES: "60° Spares",
  SKIRT_SPARES: "Skirt Spares",
  DELTA_RING_SPARES: "Delta Ring Spares",
  CENTER_60_SPARES: "Center 60° Spares",
  ELBOW_SHIELD_SPARES: "Elbow Shield Spares",
} as const;

/**
 * Mapping from spares container PartId to display name
 * Uses Partial<Record<PartId, string>> since only some PartIds are spares containers
 */
export const SPARES_CONTAINER_NAMES: Partial<Record<PartId, string>> = {
  [PART_IDS.SPIDER_SPARES]: CONTAINER_NAMES.SPIDER_SPARES,
  [PART_IDS.FOURTH_HOLE_PANEL_SPARES]: CONTAINER_NAMES.FOURTH_HOLE_PANEL_SPARES,
  [PART_IDS.FOURTH_HOLE_ELBOW_SPARES]: CONTAINER_NAMES.FOURTH_HOLE_ELBOW_SPARES,
  [PART_IDS.FIFTY_FIVE_SPARES]: CONTAINER_NAMES.FIFTY_FIVE_SPARES,
  [PART_IDS.SIXTY_SPARES]: CONTAINER_NAMES.SIXTY_SPARES,
  [PART_IDS.SKIRT_SPARES]: CONTAINER_NAMES.SKIRT_SPARES,
  [PART_IDS.DELTA_RING_SPARES]: CONTAINER_NAMES.DELTA_RING_SPARES,
  [PART_IDS.CENTER_60_SPARES]: CONTAINER_NAMES.CENTER_60_SPARES,
  [PART_IDS.ELBOW_SHIELD_SPARES]: CONTAINER_NAMES.ELBOW_SHIELD_SPARES,
};

/**
 * Ordered list of spares container IDs for consistent display
 */
export const SPARES_CONTAINER_ORDER = [
  PART_IDS.SPIDER_SPARES,
  PART_IDS.FOURTH_HOLE_PANEL_SPARES,
  PART_IDS.FOURTH_HOLE_ELBOW_SPARES,
  PART_IDS.FIFTY_FIVE_SPARES,
  PART_IDS.SIXTY_SPARES,
  PART_IDS.SKIRT_SPARES,
  PART_IDS.DELTA_RING_SPARES,
  PART_IDS.CENTER_60_SPARES,
  PART_IDS.ELBOW_SHIELD_SPARES,
] as const;

/**
 * Check if a container ID is a spare parts container
 * (Spider Spares, 4th Hole Panel Spares, etc.)
 */
export const isSpareContainer = (containerId: number | undefined): boolean => {
  if (containerId === undefined) return false;
  return (SPARES_CONTAINER_ORDER as readonly number[]).includes(containerId);
};
